import type { KnowledgeAllowance, KnowledgeBase } from '@ralph/contracts'
import clsx from 'clsx'
import { ArrowRightLeft, BookOpen, PencilLine, Trash2, TriangleAlert } from 'lucide-react'
import { useId, useState } from 'react'
import { type OwnerChoice, OwnerPicker, ownerRefOf } from '@/components/groups/OwnerPicker.tsx'
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/Dialog.tsx'
import { Button, Input, Meter, Textarea } from '@/components/ui/primitives.tsx'
import {
  useCreateKnowledgeBase,
  useDeleteKnowledgeBase,
  useMoveKnowledgeBase,
  useUpdateKnowledgeBase
} from '@/hooks/use-knowledge-bases.ts'
import { useI18n } from '@/i18n/index.ts'
import {
  confirmsName,
  exceedsLimits,
  limitsSentence,
  type OwnerAllowance,
  ownerAllowance,
  ownerRef
} from './knowledge-base.ts'
import { asSentence, messageOf, useKnowledgeViewer } from './knowledge-viewer.ts'

/**
 * The knowledge base's dialogs: making one, moving it, renaming it, deleting it (PRD-26).
 *
 * Every refusal is the server's, in its own words — a limit reached, a name not typed back — and
 * each is rendered where the person is looking rather than as a generic failure. The limits of
 * the chosen owner are read before anything is sent (D-41), so a refusal is the exception rather
 * than the way to find out.
 *
 * All four are `Dialog`s: focus moves in, Tab wraps, Escape closes, and focus returns to the
 * control that opened them.
 */

// ---------------------------------------------------------------------------
// Limits, as a meter and a sentence
// ---------------------------------------------------------------------------

const OwnerLimits = ({ limits, unknown }: { limits: OwnerAllowance; unknown?: string }) => {
  // Read inside the body, never captured beside the module (PRD-27 F-3, LOC-012).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const full = limits.kind === 'known' && limits.full
  return (
    <div className="rounded-tile border border-rule bg-fg/[0.02] px-3 py-2.5">
      {limits.kind === 'known' && (
        <Meter
          label={t.limits.meter}
          value={t.limits.meterValue(fmt, limits.used, limits.bases)}
          fill={limits.used / Math.max(1, limits.bases)}
          tone={full ? 'alert' : 'primary'}
        />
      )}
      <p
        className={clsx(
          'flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.5]',
          limits.kind === 'known' && 'mt-2',
          full ? 'text-alert' : 'text-fg-muted'
        )}
      >
        {full && <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />}
        <span>{limits.kind === 'unknown' && unknown ? unknown : limitsSentence(fmt, limits)}</span>
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// New knowledge base
// ---------------------------------------------------------------------------

export const NewBaseDialog = ({
  allowance,
  onClose,
  onCreated
}: {
  allowance: KnowledgeAllowance | null
  onClose: () => void
  onCreated: (baseId: string) => void
}) => {
  const t = useI18n().t.knowledge
  const titleId = useId()
  const nameId = useId()
  const nameHintId = useId()
  const descriptionId = useId()
  const viewer = useKnowledgeViewer()
  const create = useCreateKnowledgeBase()
  const move = useMoveKnowledgeBase()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [owner, setOwner] = useState<OwnerChoice | null>(
    viewer.id ? { kind: 'user', id: viewer.id, name: viewer.displayName } : null
  )
  const [notMoved, setNotMoved] = useState<{ baseId: string; to: string; reason: string } | null>(null)

  const chosen = owner ? ownerRefOf(owner) : null
  const limits = ownerAllowance(allowance, chosen, viewer.id)
  const full = limits.kind === 'known' && limits.full
  // An administrator may give a new base to another person: it is made theirs, then moved.
  const giveTo = owner?.kind === 'user' && owner.id !== viewer.id ? owner : null
  const busy = create.isPending || move.isPending

  const submit = () => {
    if (!owner || !chosen || name.trim() === '' || full || busy) return
    create.mutate(
      {
        name: name.trim(),
        description: description.trim(),
        ...(chosen.kind === 'group' ? { owner: chosen } : {})
      },
      {
        onSuccess: (created) => {
          if (!giveTo) {
            onCreated(created.id)
            return
          }
          move.mutate(
            { baseId: created.id, to: { kind: 'user', id: giveTo.id } },
            {
              onSuccess: () => onCreated(created.id),
              onError: (error) =>
                setNotMoved({
                  baseId: created.id,
                  to: giveTo.name,
                  reason: asSentence(messageOf(error, t.genericError))
                })
            }
          )
        }
      }
    )
  }

  return (
    <Dialog
      titleId={titleId}
      icon={<BookOpen size={14} />}
      title={t.create.title}
      lead={t.create.lead}
      onClose={onClose}
      width="lg"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <DialogBody>
          <div>
            <label htmlFor={nameId} className="mb-1 block text-[13px] font-semibold">
              {t.create.name}
            </label>
            <Input
              id={nameId}
              value={name}
              maxLength={120}
              required
              aria-describedby={nameHintId}
              placeholder={t.create.namePlaceholder}
              onChange={(event) => setName(event.target.value)}
            />
            <p id={nameHintId} className="mt-1 text-[12px] text-fg-subtle">
              {t.create.nameHint}
            </p>
          </div>

          <div>
            <label htmlFor={descriptionId} className="mb-1 block text-[13px] font-semibold">
              {t.create.description}
            </label>
            <Textarea
              id={descriptionId}
              value={description}
              rows={2}
              maxLength={2000}
              placeholder={t.create.descriptionPlaceholder}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          {viewer.id && (
            <OwnerPicker
              value={owner}
              onChange={(next) => {
                setOwner(next)
                setNotMoved(null)
              }}
              viewer={{ id: viewer.id, displayName: viewer.displayName, managedGroups: viewer.managedGroups }}
              allowAnyone={viewer.isAdmin}
              disabled={busy}
            />
          )}

          <OwnerLimits limits={limits} {...(giveTo ? { unknown: t.create.forPerson(giveTo.name) } : {})} />

          <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{t.create.afterwards}</p>

          {notMoved ? (
            <div
              role="alert"
              className="rounded-tile border border-alert/40 bg-alert-soft px-3 py-2.5 text-[12.5px] leading-relaxed"
            >
              <p>{t.create.notMoved(notMoved.to, notMoved.reason)}</p>
              <Button size="sm" className="mt-2" onClick={() => onCreated(notMoved.baseId)}>
                {t.create.open}
              </Button>
            </div>
          ) : (
            create.isError && (
              <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
                {asSentence(messageOf(create.error, t.genericError))}
              </p>
            )
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t.create.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy || name.trim() === '' || !owner || full || notMoved !== null}
          >
            {busy ? t.create.submitting : t.create.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Moving it (D-32)
// ---------------------------------------------------------------------------

export const MoveBaseDialog = ({
  base,
  allowance,
  onClose
}: {
  base: KnowledgeBase
  allowance: KnowledgeAllowance | null
  onClose: () => void
}) => {
  const titleId = useId()
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const viewer = useKnowledgeViewer()
  const move = useMoveKnowledgeBase()
  const [choice, setChoice] = useState<OwnerChoice | null>(null)

  const chosen = choice ? ownerRefOf(choice) : null
  const limits = ownerAllowance(allowance, chosen, viewer.id)
  const full = limits.kind === 'known' && limits.full
  const over = choice !== null && exceedsLimits(base, limits)

  const maintainers =
    choice === null
      ? null
      : choice.kind === 'group'
        ? t.move.maintainers.group(choice.group.name)
        : choice.id === viewer.id
          ? t.move.maintainers.you
          : t.move.maintainers.person(choice.name)

  const submit = () => {
    if (!chosen || full || move.isPending) return
    move.mutate({ baseId: base.id, to: chosen }, { onSuccess: onClose })
  }

  return (
    <Dialog
      titleId={titleId}
      icon={<ArrowRightLeft size={14} />}
      title={t.move.title(base.name)}
      lead={t.move.lead}
      onClose={onClose}
      width="lg"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <DialogBody>
          {viewer.id && (
            <OwnerPicker
              value={choice}
              onChange={setChoice}
              viewer={{ id: viewer.id, displayName: viewer.displayName, managedGroups: viewer.managedGroups }}
              current={ownerRef(base.owner)}
              // Only an administrator moves a base to a person; anybody else only to a group they
              // manage (D-32), so "You" is not a choice the server would take from them.
              includeSelf={viewer.isAdmin}
              allowAnyone={viewer.isAdmin}
              disabled={move.isPending}
            />
          )}

          {choice === null ? (
            <p className="text-[12.5px] text-fg-subtle">{t.move.choose}</p>
          ) : (
            <div className="space-y-2">
              <p className="text-pretty text-[13px] leading-[1.5]">{maintainers}</p>
              <OwnerLimits limits={limits} unknown={t.move.unknown} />
              {over && limits.kind === 'known' && (
                <p className="flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.5] text-alert">
                  <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
                  {t.move.overLimit(fmt, limits.documentsPerBase, fmt.formatBytes(limits.storagePerBaseBytes))}
                </p>
              )}
              {choice.kind === 'group' && base.connectedFolders.total > 0 && (
                <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{t.move.folders}</p>
              )}
            </div>
          )}

          {move.isError && (
            <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
              {asSentence(messageOf(move.error, t.genericError))}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t.move.cancel}
          </Button>
          <Button type="submit" variant="primary" disabled={choice === null || full || move.isPending}>
            {move.isPending ? t.move.submitting : t.move.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Renaming it, and what it says it holds
// ---------------------------------------------------------------------------

export const EditBaseDialog = ({ base, onClose }: { base: KnowledgeBase; onClose: () => void }) => {
  const t = useI18n().t.knowledge
  const titleId = useId()
  const nameId = useId()
  const descriptionId = useId()
  const update = useUpdateKnowledgeBase()
  const [name, setName] = useState(base.name)
  const [description, setDescription] = useState(base.description)
  const unchanged = name.trim() === base.name && description.trim() === base.description.trim()

  return (
    <Dialog
      titleId={titleId}
      icon={<PencilLine size={14} />}
      title={t.edit.title(base.name)}
      lead={t.edit.lead}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim() === '' || unchanged) return
          update.mutate({ baseId: base.id, name: name.trim(), description: description.trim() }, { onSuccess: onClose })
        }}
      >
        <DialogBody>
          <div>
            <label htmlFor={nameId} className="mb-1 block text-[13px] font-semibold">
              {t.create.name}
            </label>
            <Input
              id={nameId}
              value={name}
              maxLength={120}
              required
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor={descriptionId} className="mb-1 block text-[13px] font-semibold">
              {t.create.description}
            </label>
            <Textarea
              id={descriptionId}
              value={description}
              rows={3}
              maxLength={2000}
              placeholder={t.create.descriptionPlaceholder}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {update.isError && (
            <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
              {asSentence(messageOf(update.error, t.genericError))}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t.edit.cancel}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending || name.trim() === '' || unchanged}>
            {update.isPending ? t.edit.submitting : t.edit.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Deleting it: its consequences first, then its name typed back
// ---------------------------------------------------------------------------

export const DeleteBaseDialog = ({
  base,
  onClose,
  onDeleted
}: {
  base: KnowledgeBase
  onClose: () => void
  onDeleted: (name: string) => void
}) => {
  const titleId = useId()
  const confirmId = useId()
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const remove = useDeleteKnowledgeBase()
  const [typed, setTyped] = useState('')
  const matches = confirmsName(typed, base.name)
  const hasRequests = base.pendingRequest !== null || base.pendingLendingRequests > 0

  return (
    <Dialog
      titleId={titleId}
      icon={<Trash2 size={14} />}
      title={t.remove.title(base.name)}
      lead={t.remove.lead}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (!matches || remove.isPending) return
          remove.mutate({ baseId: base.id, confirmName: typed.trim() }, { onSuccess: () => onDeleted(base.name) })
        }}
      >
        <DialogBody>
          <ul className="space-y-2 text-pretty text-[13px] leading-[1.5]">
            <li>{t.remove.documents(fmt, base.documentCount)}</li>
            {base.connectedFolders.total > 0 && <li>{t.remove.folders(fmt, base.connectedFolders.total)}</li>}
            <li>
              {base.attachedAgents.length === 0 ? (
                t.remove.noAgents
              ) : (
                <>
                  {t.remove.agents}
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {base.attachedAgents.map((agent) => (
                      <li
                        key={agent.agentId}
                        className="rounded-full border border-rule bg-card-solid px-2.5 py-[3px] text-[12.5px] font-medium"
                      >
                        {agent.displayName}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </li>
            {hasRequests && <li>{t.remove.requests}</li>}
          </ul>

          <div>
            <label htmlFor={confirmId} className="mb-1 block text-[13px] font-semibold">
              {t.remove.typeName(base.name)}
            </label>
            <Input
              id={confirmId}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>

          {remove.isError && (
            <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
              {asSentence(messageOf(remove.error, t.genericError))}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t.remove.cancel}
          </Button>
          <Button type="submit" variant="danger" disabled={!matches || remove.isPending}>
            {remove.isPending ? t.remove.submitting : t.remove.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
